import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import { emphasize, styled, useTheme } from "@mui/material/styles";
import { IconButton, Tooltip, useMediaQuery } from "@mui/material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import ManualViewerDialog from "./ManualViewerDialog";

const StyledCrumb = styled(Chip)(({ theme }) => ({
  height: 28,
  fontSize: 12.5,
  fontWeight: 500,
  cursor: "pointer",
  backgroundColor:
    theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
  color: theme.palette.text.primary,
  transition: "background-color 0.15s ease, color 0.15s ease",
  "&:hover, &:focus": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? emphasize(theme.palette.grey[800], 0.08)
        : emphasize(theme.palette.grey[100], 0.08),
    color: theme.palette.primary.main,
  },
  "&:active": { boxShadow: theme.shadows[1] },
}));

const ROUTE_MAP = {
  // Top-level
  "home":                                             { label: "Inicio",                  parent: null },
  "legalFileHome":                                    { label: "Expedientes",              parent: null },
  "paymentOrder":                                     { label: "Órdenes de Pago",          parent: null },
  "lawsuit":                                          { label: "Títulos Electrónicos",     parent: null },
  "lawyer":                                           { label: "Abogados",                 parent: null },
  "user":                                             { label: "Usuarios",                 parent: null },
  "security":                                         { label: "Seguridad",                parent: null },
  "settings":                                         { label: "Configuración",            parent: null },
  "job/history":                                      { label: "Historial de Procesos",    parent: null },
  "contributors/arca":                                { label: "ARCA",                     parent: null },
  "workspace":                                        { label: "Área de Trabajo",          parent: null },

  // Workspace
  "workspace/:id":                                    { label: "Detalle de Tarea",         parent: "workspace" },

  // Expedientes
  "legalFile":                                        { label: "Gestionar",                parent: "legalFileHome" },
  "legal/file/search":                                { label: "Buscar Expediente",        parent: "legalFile" },
  "legal/file/:id":                                   { label: "Detalle",                  parent: "legalFile" },
  "legalFile/charge":                                 { label: "Cargar",                   parent: "legalFile" },
  "legalFile/manager":                                { label: "Listado",                  parent: "legalFile" },
  "legalFile/failed":                                 { label: "Fallidos",                 parent: "legalFile" },

  // Escritos
  "legalFile/attachments":                            { label: "Escritos",                 parent: "legalFile" },
  "legalFile/attachments/manager":                    { label: "Gestionar",                parent: "legalFile/attachments" },
  "legalFile/attachments/generate/upload":            { label: "Generar y Subir",          parent: "legalFile/attachments" },
  "legalFile/upload/attachments":                     { label: "Subir",                    parent: "legalFile/attachments" },
  "legalFile/attachments/upload/sac":                 { label: "Subir a SAC",              parent: "legalFile/attachments" },
  "legalFile/upload/aportes":                         { label: "Planilla de Aportes",      parent: "legalFile/attachments" },
  "legalFile/attachments/report":                     { label: "Reporte",                  parent: "legalFile/attachments" },

  // Adjuntos
  "legalFile/documents":                              { label: "Adjuntos",                 parent: "legalFileHome" },

  // Reportes
  "legalFile/report/menu":                            { label: "Reportes",                 parent: "legalFileHome" },
  "legalFile/report":                                 { label: "Reporte General",          parent: "legalFile/report/menu" },
  "legalFile/report/file":                            { label: "Reporte Expediente",       parent: "legalFile/report/menu" },
  "legalFile/failed/report":                          { label: "Reporte Fallidos",         parent: "legalFile/report/menu" },
  "legalFile/report/basic":                           { label: "Inventario Expedientes",   parent: "legalFile/report/menu" },
  "legalFile/report/operations":                      { label: "Reporte Operaciones",      parent: "legalFile/report/menu" },

  // Novedades
  "legalFile/news":                                   { label: "Novedades",                parent: "legalFileHome" },
  "legalFile/news/generate":                          { label: "Generar",                  parent: "legalFile/news" },
  "legalFile/news/failed":                            { label: "Fallidas",                 parent: "legalFile/news" },
  "legalFile/news/txt":                               { label: "TXT",                      parent: "legalFile/news" },
  "legalFile/judicial/import":                        { label: "Importar",                 parent: "legalFile/news" },

  // Partes y Clientes
  "legalFile/parties":                                { label: "Partes y Clientes",        parent: "legalFileHome" },
  "legalFile/parties/search":                         { label: "Buscar",                   parent: "legalFile/parties" },
  "legalFile/parties/clients":                        { label: "Clientes",                 parent: "legalFile/parties" },

  // Cuentas Judiciales
  "legalFile/judicial/accounts":                      { label: "Cuentas Judiciales",       parent: "legalFileHome" },
  "legalFile/judicial/accounts/charge":               { label: "Cargar",                   parent: "legalFile/judicial/accounts" },
  "legalFile/judicial/accounts/manager":              { label: "Gestionar",                parent: "legalFile/judicial/accounts" },
  "legalFile/judicial/accounts/report":               { label: "Reporte",                  parent: "legalFile/judicial/accounts" },

  // Cédulas de Notificación
  "legalFile/notification/certificates":              { label: "Cédulas de Notificación",  parent: "legalFileHome" },
  "legalFile/notification/certificates/upload":       { label: "Subir",                    parent: "legalFile/notification/certificates" },
  "legalFile/notification/certificates/generate":     { label: "Generar",                  parent: "legalFile/notification/certificates" },
  "legalFile/notification/certificates/manager":      { label: "Gestionar",                parent: "legalFile/notification/certificates" },
  "legalFile/notification/certificates/barcode":      { label: "Escáner",                  parent: "legalFile/notification/certificates" },
  "legalFile/notification/certificates/update":       { label: "Actualizar",               parent: "legalFile/notification/certificates" },
  "legalFile/notification/certificates/report":       { label: "Reporte",                  parent: "legalFile/notification/certificates" },
  "legalFile/notification/certificates/failures":     { label: "Fallidas",                 parent: "legalFile/notification/certificates" },
  "legalFile/notification/certificates/unlinked":     { label: "Sin Vincular",             parent: "legalFile/notification/certificates" },
  "legalFile/notification/certificates/uploadOnline": { label: "Subir Online",             parent: "legalFile/notification/certificates" },

  // Órdenes de Pago
  "paymentOrder/manager":                             { label: "Gestionar",                parent: "paymentOrder" },
  "paymentOrder/import":                              { label: "Importar",                 parent: "paymentOrder" },
  "paymentOrder/generate/byLawyer":                   { label: "Por Abogado",              parent: "paymentOrder" },
  "paymentOrder/generate/byFile":                     { label: "Por Expediente",           parent: "paymentOrder" },
  "paymentOrder/report/menu":                         { label: "Reportes",                 parent: "paymentOrder" },
  "paymentOrder/report/txt":                          { label: "Descargar Órdenes",        parent: "paymentOrder/report/menu" },
  "paymentOrder/report/retentions":                   { label: "Reporte de Retenciones",   parent: "paymentOrder/report/menu" },

  // Seguridad
  "roles":                                            { label: "Roles",                    parent: "security" },
  "security/audit":                                   { label: "Auditoría",                parent: "security" },

  // Configuración
  "branches":                                         { label: "Sucursales",               parent: "settings" },
  "settings/general":                                 { label: "General",                  parent: "settings" },
  "attachment-types-config":                          { label: "Tipos de Escritos",        parent: "settings" },
  "attachment-source-types-config":                   { label: "Fuentes de Escritos",      parent: "settings" },
  "settings/lawsuit-tax-status":                      { label: "Estados del Sistema",       parent: "settings" },
  "settings-operation-types":                         { label: "Tipos de Operaciones",     parent: "settings" },
  "settings-auctioneers":                             { label: "Martilleros",              parent: "settings" },
  "settings-legal-file-stages":                       { label: "Etapas de Expediente",     parent: "settings" },
  "settings-sentence-markers":                        { label: "Marcadores de Sentencia Municipalidad",  parent: "settings" },
  "settings-liquidation":                             { label: "Liquidaciones Municipalidad", parent: "settings" },
  "settings-individual-liquidation":                  { label: "Liquidaciones Comunes", parent: "settings" },

  // ARCA
  "contributors/arca/manager":                        { label: "Gestionar",                parent: "contributors/arca" },
  "contributors/arca/generate/file":                  { label: "Generar Archivo",          parent: "contributors/arca" },
  "contributors/arca/charge/file":                    { label: "Cargar Archivo",           parent: "contributors/arca" },

  // Liquidaciones
  "liquidations":                                     { label: "Liquidaciones",            parent: null },
  "liquidations/manager":                             { label: "Gestionar",                parent: "liquidations" },
  "liquidations/municipality-report":                 { label: "Planilla Municipalidad",   parent: "liquidations" },
  "liquidations/liquidation-report":                  { label: "Planilla de Liquidaciones", parent: "liquidations" },
  "individual-liquidations/manager":                  { label: "Liquidaciones Comunes", parent: "liquidations" },
  "individual-liquidations/report":                   { label: "Planilla Liquidaciones Comunes", parent: "liquidations" },

  // Demandas
  "lawsuit/upload":                                   { label: "Subir",                    parent: "lawsuit" },
  "lawsuit/upload/file/update":                       { label: "Actualizar Archivo",       parent: "lawsuit" },
  "lawsuit/upload/file":                              { label: "Subir Archivo",            parent: "lawsuit" },
  "lawsuit/get/lawyers":                              { label: "Abogados",                 parent: "lawsuit" },
  "lawsuit/lawyers/:lawyerId":                        { label: "Títulos del Abogado",      parent: "lawsuit" },
  "lawsuit/all":                                      { label: "Todos los Títulos",        parent: "lawsuit" },
  "lawsuit/failure":                                  { label: "Fallidas",                 parent: "lawsuit" },
  "lawsuit/unlinked":                                 { label: "Sin Vincular",             parent: "lawsuit" },
  "lawsuit/upload/historical":                        { label: "Cargar Históricas",        parent: "lawsuit" },
  "lawsuit/update/juc":                               { label: "Actualizar Estado y ID",   parent: "lawsuit" },
  "lawsuit/export/data":                              { label: "Exportar Datos",           parent: "lawsuit" },
  "lawsuit/import/concluded":                         { label: "Importar Concluidas",      parent: "lawsuit" },
  "lawsuit/associate/legal/file":                     { label: "Asociar Expediente",       parent: "lawsuit" },
};

// Maps a path prefix to a manual key (must match keys in src/manuals/index.jsx).
// Order matters: more specific prefixes first.
const MANUAL_PREFIX_MAP = [
  { prefix: "liquidations",                   manualKey: "liquidations"  },
  { prefix: "legalFile",                      manualKey: "legalFiles"    },
  { prefix: "legal/file",                     manualKey: "legalFiles"    },
  { prefix: "paymentOrder",                   manualKey: "paymentOrders" },
  { prefix: "lawsuit",                        manualKey: "lawsuits"      },
  { prefix: "lawyer",                         manualKey: "lawyers"       },
  { prefix: "user",                           manualKey: "users"         },
  { prefix: "security",                       manualKey: "security"      },
  { prefix: "roles",                          manualKey: "security"      },
  { prefix: "security/audit",                 manualKey: "security"      },
  { prefix: "settings",                       manualKey: "settings"      },
  { prefix: "settings/general",               manualKey: "settings"      },
  { prefix: "branches",                       manualKey: "settings"      },
  { prefix: "attachment-types-config",        manualKey: "settings"      },
  { prefix: "attachment-source-types-config", manualKey: "settings"      },
  { prefix: "settings/lawsuit-tax-status",     manualKey: "settings"      },
  { prefix: "settings-operation-types",       manualKey: "settings"      },
  { prefix: "settings-auctioneers",           manualKey: "settings"      },
  { prefix: "settings-legal-file-stages",     manualKey: "settings"      },
  { prefix: "settings-sentence-markers",      manualKey: "liquidations"  },
  { prefix: "settings-individual-liquidation", manualKey: "liquidations" },
  { prefix: "settings-liquidation",           manualKey: "liquidations"  },
  { prefix: "individual-liquidations",        manualKey: "liquidations"  },
  { prefix: "job/history",                    manualKey: "jobHistory"    },
  { prefix: "workspace",                      manualKey: "workspace"     },
];

function findManual(pathname) {
  const path = pathname.startsWith("/") ? pathname.slice(1) : pathname;
  return MANUAL_PREFIX_MAP.find(
    ({ prefix }) => path === prefix || path.startsWith(prefix + "/"),
  ) ?? null;
}

function matchRoute(pathname) {
  const path = pathname.startsWith("/") ? pathname.slice(1) : pathname;
  if (!path) return null;
  if (ROUTE_MAP[path]) return path;

  const pathSegs = path.split("/");
  for (const key of Object.keys(ROUTE_MAP)) {
    const keySegs = key.split("/");
    if (keySegs.length !== pathSegs.length) continue;
    if (keySegs.every((seg, i) => seg.startsWith(":") || seg === pathSegs[i])) {
      return key;
    }
  }
  return null;
}

function buildTrail(routeKey) {
  const trail = [];
  let current = routeKey;

  while (current && ROUTE_MAP[current]) {
    if (current !== "home") {
      trail.unshift({
        label: ROUTE_MAP[current].label,
        path: `/${current}`,
        isDynamic: current.includes(":"),
      });
    }
    current = ROUTE_MAP[current].parent;
  }

  return trail;
}

export default function Breadcrumb() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";

  const [manualOpen, setManualOpen] = useState(false);

  const routeKey = matchRoute(location.pathname);
  if (!routeKey || routeKey === "home") return null;

  const trail = buildTrail(routeKey);
  const manualEntry = findManual(location.pathname);
  const showEllipsis = isMobile && trail.length > 2;
  const visibleTrail = showEllipsis ? trail.slice(-2) : trail;

  const lastBg = isDark ? "rgba(99,102,241,0.18)" : "rgba(99,102,241,0.09)";

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: 72,
          zIndex: (t) => t.zIndex.drawer,
          px: { xs: 2, sm: 3, md: 4 },
          height: 44,
          display: "flex",
          alignItems: "center",
          background: isDark ? "rgba(15,23,42,0.75)" : "rgba(255,255,255,0.75)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
        }}
      >
        <Breadcrumbs
          separator={
            <NavigateNextRoundedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
          }
          aria-label="breadcrumb"
          sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5 } }}
        >
          {/* Home — icono solamente */}
          <StyledCrumb
            icon={<HomeRoundedIcon />}
            label=""
            onClick={() => navigate("/home")}
            sx={{
              px: 0,
              minWidth: 32,
              "& .MuiChip-label": { display: "none" },
              "& .MuiChip-icon": { fontSize: "16px !important", mx: "auto", ml: "10px", mr: "10px" },
            }}
          />

          {showEllipsis && (
            <StyledCrumb label="…" sx={{ cursor: "default" }} />
          )}

          {visibleTrail.map((item, index) => {
            const isLast = index === visibleTrail.length - 1;
            const clickable = !isLast && !item.isDynamic;
            return (
              <StyledCrumb
                key={`${item.path}-${index}`}
                label={item.label}
                onClick={clickable ? () => navigate(item.path) : undefined}
                sx={{
                  fontWeight: isLast ? 700 : 500,
                  cursor: isLast || item.isDynamic ? "default" : "pointer",
                  ...(isLast && {
                    bgcolor: lastBg,
                    color: "primary.main",
                    "&:hover, &:focus": { bgcolor: lastBg, color: "primary.main" },
                  }),
                }}
              />
            );
          })}
        </Breadcrumbs>

        {manualEntry && (
          <>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Ver manual del módulo" placement="left">
              <IconButton
                onClick={() => setManualOpen(true)}
                size="small"
                sx={{
                  borderRadius: 2,
                  color: "text.disabled",
                  transition: "color .15s ease",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <MenuBookRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {manualEntry && (
        <ManualViewerDialog
          open={manualOpen}
          onClose={() => setManualOpen(false)}
          manualKey={manualEntry.manualKey}
        />
      )}
    </>
  );
}
